import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBusinessProfile } from '../../contexts/BusinessProfileContext';
import { useToast } from '../../contexts/ToastContext';
import { businessProfileService } from '../../services/businessProfileService';
import { tpayService } from '../../services/tpayService';
import { ApiError } from '../../services/apiClient';
import spottoLogo from '../../assets/spotto-logo.png';
import type { LegalForm, TPayCategory, BusinessProfileFormData } from '../../types/businessProfile';

export const BusinessProfileSetup: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, isAuthenticated, isLoading: authLoading, signOut, refreshAuth } = useAuth();
  const { hasProfile, refetch: refetchProfile } = useBusinessProfile();
  const { showToast } = useToast();

  // Form fields
  const [companyName, setCompanyName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [nip, setNip] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [categoryId, setCategoryId] = useState<number>(0);
  const [legalForm, setLegalForm] = useState<number>(0);
  const [contactPersonName, setContactPersonName] = useState('');
  const [contactPersonSurname, setContactPersonSurname] = useState('');
  const [website, setWebsite] = useState('');

  // Dictionaries
  const [legalForms, setLegalForms] = useState<LegalForm[]>([]);
  const [categories, setCategories] = useState<TPayCategory[]>([]);
  const [loadingDictionaries, setLoadingDictionaries] = useState(true);

  // UI state
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if not authenticated or already has profile
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
    // If user already has a profile, redirect to dashboard
    if (hasProfile) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, hasProfile, navigate]);

  // Load dictionaries
  useEffect(() => {
    const loadDictionaries = async () => {
      if (!token) return;

      try {
        const [legalFormsData, categoriesData] = await Promise.all([
          tpayService.getLegalForms(token),
          tpayService.getCategories(token),
        ]);
        setLegalForms(legalFormsData);
        setCategories(categoriesData);
      } catch (err) {
        console.error('Failed to load dictionaries:', err);
      } finally {
        setLoadingDictionaries(false);
      }
    };

    loadDictionaries();
  }, [token]);

  const validateNip = (value: string): boolean => {
    // Remove any non-digit characters
    const digits = value.replace(/\D/g, '');
    return digits.length === 10;
  };

  const validateForm = (): boolean => {
    if (!companyName.trim()) {
      setError(t('businessSetup.validation.companyNameRequired'));
      return false;
    }
    if (!nip.trim()) {
      setError(t('businessSetup.validation.nipRequired'));
      return false;
    }
    if (!validateNip(nip)) {
      setError(t('businessSetup.validation.nipInvalid'));
      return false;
    }
    if (!displayName.trim()) {
      setError(t('businessSetup.validation.displayNameRequired'));
      return false;
    }
    if (!address.trim()) {
      setError(t('businessSetup.validation.addressRequired'));
      return false;
    }
    if (!city.trim()) {
      setError(t('businessSetup.validation.cityRequired'));
      return false;
    }
    if (!postalCode.trim()) {
      setError(t('businessSetup.validation.postalCodeRequired'));
      return false;
    }
    if (!email.trim()) {
      setError(t('businessSetup.validation.emailRequired'));
      return false;
    }
    if (!email.includes('@')) {
      setError(t('businessSetup.validation.emailInvalid'));
      return false;
    }
    if (!phoneNumber.trim()) {
      setError(t('businessSetup.validation.phoneRequired'));
      return false;
    }
    if (!categoryId || categoryId === 0) {
      setError(t('businessSetup.validation.categoryRequired'));
      return false;
    }
    if (!legalForm || legalForm === 0) {
      setError(t('businessSetup.validation.legalFormRequired'));
      return false;
    }
    if (!contactPersonName.trim()) {
      setError(t('businessSetup.validation.contactPersonNameRequired'));
      return false;
    }
    if (!contactPersonSurname.trim()) {
      setError(t('businessSetup.validation.contactPersonSurnameRequired'));
      return false;
    }
    if (!website.trim()) {
      setError(t('businessSetup.validation.websiteRequired'));
      return false;
    }
    // Basic URL validation
    try {
      new URL(website.trim());
    } catch {
      setError(t('businessSetup.validation.websiteInvalid'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    if (!token) {
      setError(t('businessSetup.errors.createFailed'));
      return;
    }

    setIsSubmitting(true);

    try {
      const formData: BusinessProfileFormData = {
        companyName: companyName.trim(),
        displayName: displayName.trim(),
        nip: nip.replace(/\D/g, ''),
        address: address.trim(),
        city: city.trim(),
        postalCode: postalCode.trim(),
        email: email.trim(),
        phoneNumber: phoneNumber.trim(),
        phoneCountry: 'PL',
        categoryId,
        legalForm,
        regon: '',
        krs: '',
        website: website.trim(),
        contactPersonName: contactPersonName.trim(),
        contactPersonSurname: contactPersonSurname.trim(),
      };

      await businessProfileService.createProfile(token, formData);

      // Creating a profile grants the "Business" role on the backend. Refresh the
      // token so it carries that role - otherwise role-gated endpoints (e.g. the
      // scheduler dashboard) reject the still-stale token. A refresh failure here
      // is non-fatal: the profile exists and the dashboard tolerates token lag.
      try {
        await refreshAuth();
      } catch (refreshErr) {
        console.error('Token refresh after profile creation failed:', refreshErr);
      }

      // Refetch the profile to update context state
      await refetchProfile();
      showToast('success', t('businessProfile.messages.createSuccess'));
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || t('businessSetup.errors.createFailed'));
      } else {
        setError(t('businessSetup.errors.createFailed'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading || loadingDictionaries) {
    return (
      <div className="min-h-screen bg-[#23262b] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4d63ac]" />
          <p className="text-[#9aa3b2]">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#23262b] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo and Logout */}
        <div className="text-center mb-8">
          <img
            src={spottoLogo}
            alt="Spotto Logo"
            className="h-12 w-auto mx-auto mb-4"
          />
          <button
            onClick={signOut}
            className="text-sm text-[#9aa3b2] hover:text-[#c5cde0] transition-colors"
          >
            {t('common.logout', 'Wyloguj się')}
          </button>
        </div>

        {/* Setup Form */}
        <div className="bg-[#2c3036] rounded-2xl border border-white/[0.08] p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-white mb-2">{t('businessSetup.title')}</h2>
          <p className="text-[#9aa3b2] mb-6">{t('businessSetup.subtitle')}</p>

          {error && (
            <div className="mb-6 p-4 bg-[#c06f57]/10 border border-[#c06f57]/30 rounded-lg">
              <p className="text-sm text-[#e1a08a]">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Company Name and Display Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.companyName')} *
                </label>
                <input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.displayName')} *
                </label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* NIP */}
            <div>
              <label htmlFor="nip" className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('businessSetup.nip')} *
              </label>
              <input
                id="nip"
                type="text"
                value={nip}
                onChange={(e) => setNip(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                placeholder="0000000000"
                maxLength={13}
                disabled={isSubmitting}
              />
            </div>

            {/* Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('businessSetup.address')} *
              </label>
              <input
                id="address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                disabled={isSubmitting}
              />
            </div>

            {/* City and Postal Code */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.city')} *
                </label>
                <input
                  id="city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.postalCode')} *
                </label>
                <input
                  id="postalCode"
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  placeholder="00-000"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.email')} *
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.phone')} *
                </label>
                <input
                  id="phoneNumber"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  placeholder="+48 123 456 789"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Category and Legal Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="categoryId" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.category')} *
                </label>
                <select
                  id="categoryId"
                  value={categoryId}
                  onChange={(e) => setCategoryId(Number(e.target.value))}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  disabled={isSubmitting}
                >
                  <option value={0}>{t('businessProfile.placeholders.selectCategory')}</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="legalForm" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.legalForm')} *
                </label>
                <select
                  id="legalForm"
                  value={legalForm}
                  onChange={(e) => setLegalForm(Number(e.target.value))}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  disabled={isSubmitting}
                >
                  <option value={0}>{t('businessProfile.placeholders.selectLegalForm')}</option>
                  {legalForms.map((form) => (
                    <option key={form.id} value={form.id}>
                      {form.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contact Person */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contactPersonName" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.contactPersonName')} *
                </label>
                <input
                  id="contactPersonName"
                  type="text"
                  value={contactPersonName}
                  onChange={(e) => setContactPersonName(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label htmlFor="contactPersonSurname" className="block text-sm font-medium text-[#c5cde0] mb-2">
                  {t('businessSetup.contactPersonSurname')} *
                </label>
                <input
                  id="contactPersonSurname"
                  type="text"
                  value={contactPersonSurname}
                  onChange={(e) => setContactPersonSurname(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('businessSetup.website')} *
              </label>
              <input
                id="website"
                type="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                placeholder="https://example.com"
                disabled={isSubmitting}
              />
              <p className="mt-1 text-xs text-[#9aa3b2]">{t('businessSetup.websiteHint')}</p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 bg-[#4d63ac] hover:bg-[#5b71be] disabled:bg-[#4d63ac]/50 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:ring-offset-2 focus:ring-offset-[#2c3036] mt-6"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('businessSetup.creatingProfile')}
                </span>
              ) : (
                t('businessSetup.createProfile')
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
